import {
    Box,
    Button,
    Grid,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import React, { useState, useRef } from 'react';
import AiReviewSummary from './AiReviewSummary';
import AllReviewsModal from './AllReviewsModal';
import ReviewCard from './ReviewCard';
import ReviewsSummary from './ReviewsSummary';

interface ExperienceReviewsSectionProps {
    reviewsData: any;
}

const ExperienceReviewsSection: React.FC<ExperienceReviewsSectionProps> = ({
    reviewsData,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [showAllReviewsModal, setShowAllReviewsModal] = useState(false);
    const [selectedReviewId, setSelectedReviewId] = useState<
        number | undefined
    >();
    const [highlightedReviewIds, setHighlightedReviewIds] = useState<string[]>(
        [],
    );
    const [highlightKeyword, setHighlightKeyword] = useState<string>('');
    const [highlightSentiment, setHighlightSentiment] = useState<
        'positive' | 'negative'
    >('positive');
    const [highlightPositiveCount, setHighlightPositiveCount] = useState<
        number | undefined
    >();
    const [highlightNegativeCount, setHighlightNegativeCount] = useState<
        number | undefined
    >();
    const clearTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const { reviews, averageRatings, aiReview } = reviewsData;

    const handleShowAllReviews = () => {
        setSelectedReviewId(undefined);
        setShowAllReviewsModal(true);
    };

    const handleShowMore = (reviewId: number) => {
        setSelectedReviewId(reviewId);
        setShowAllReviewsModal(true);
    };

    const handleCloseModal = () => {
        setShowAllReviewsModal(false);
        clearTimeoutRef.current = setTimeout(() => {
            setSelectedReviewId(undefined);
            setHighlightedReviewIds([]);
            setHighlightKeyword('');
            setHighlightSentiment('positive');
            setHighlightPositiveCount(undefined);
            setHighlightNegativeCount(undefined);
        }, 1000);
    };

    const handleKeywordClick = (
        keyword: string,
        reviewIds: string[],
        sentiment: 'positive' | 'negative',
        positiveCount?: number,
        negativeCount?: number,
    ) => {
        if (clearTimeoutRef.current) {
            clearTimeout(clearTimeoutRef.current);
            clearTimeoutRef.current = null;
        }

        setHighlightKeyword(keyword);
        setHighlightedReviewIds(reviewIds);
        setHighlightSentiment(sentiment);
        setHighlightPositiveCount(positiveCount);
        setHighlightNegativeCount(negativeCount);
        setSelectedReviewId(undefined);
        setShowAllReviewsModal(true);
    };

    return (
        <>
            <Box>
                <Box pt='30px'>
                    <ReviewsSummary
                        reviews={reviews}
                        averageRatings={averageRatings}
                    />

                    {aiReview && (
                        <AiReviewSummary
                            aiReview={aiReview}
                            onKeywordClick={handleKeywordClick}
                            onShowMore={handleShowMore}
                            reviews={reviews}
                        />
                    )}

                    <Box mt={4}>
                        <Typography component={'h2'} variant='visuallyHidden'>
                            Customer reviews
                        </Typography>
                        <Grid
                            container
                            spacing={2}
                            sx={{
                                listStyleType: 'none',
                            }}
                        >
                            {reviews
                                .slice(0, isMobile ? 2 : 4)
                                .map((review: any) => (
                                    <Grid
                                        item
                                        xs={12}
                                        lg={6}
                                        key={review.id}
                                        component={'li'}
                                    >
                                        <ReviewCard
                                            review={review}
                                            onShowMore={handleShowMore}
                                        />
                                    </Grid>
                                ))}
                        </Grid>
                        {reviews.length > (isMobile ? 2 : 4) && (
                            <Box display='flex' mt={3}>
                                <Button
                                    color='primary'
                                    onClick={handleShowAllReviews}
                                    sx={{
                                        textTransform: 'none',
                                    }}
                                >
                                    {`Show all ${reviews.length} reviews`}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Box>
            </Box>

            <AllReviewsModal
                open={showAllReviewsModal}
                onClose={handleCloseModal}
                reviews={reviews}
                averageRatings={averageRatings}
                selectedReviewId={selectedReviewId}
                highlightedReviewIds={highlightedReviewIds}
                highlightKeyword={highlightKeyword}
                highlightSentiment={highlightSentiment}
                highlightPositiveCount={highlightPositiveCount}
                highlightNegativeCount={highlightNegativeCount}
            />
        </>
    );
};

export default ExperienceReviewsSection;
